/**
 * What a researcher attached to this subject, and what they excluded.
 *
 * **Thin on purpose.** A link carries the assertion it points at, why somebody attached it
 * and when — and nothing else. The source, title, snippet and content address live on the
 * assertion behind a content address, and copying them here would be a second version of
 * the truth free to drift from the first. The Profiles and Interests panels read those
 * details from the assertion itself, which is where they belong.
 *
 * **Detaching is a decision, not a delete.** The link stays, records who excluded it, and
 * remains retrievable — because deciding a piece of evidence does not belong is a finding
 * about the research, and a finding that leaves no trace is one nobody can review.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { Badge, EmptyState, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";

interface EvidenceLink {
  id: string;
  assertion_id: string;
  predicate: string;
  object_type: string;
  object_value: string;
  title: string;
  snippet: string;
  result_type: string;
  evidence_type: string;
  evidence_url: string;
  source: string;
  observed_at: string;
  extraction_confidence: number;
  information_credibility: number;
  evidence_sha256: string;
  rationale: string;
  attached_at: string;
  detached_at: string | null;
}

/** The page a link points at: the value when it is a URL, else the page it was found on. */
function linkHref(link: EvidenceLink): string {
  if (link.object_type === "url" && link.object_value !== "") return link.object_value;
  return link.evidence_url !== "" ? link.evidence_url : link.object_value;
}

/** A short, human description of what a link is, from its source and what it asserts. */
function describe(link: EvidenceLink): string {
  const kind = (link.evidence_type || link.result_type || link.predicate)
    .replaceAll("_", " ")
    .trim();
  return [link.source, kind].filter((part) => part !== "").join(" · ");
}

/** The canned rationale the search page attaches with; shown only when it is not this. */
const CANNED_RATIONALE = "Attached from a search result.";

/**
 * The evidence panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectEvidencePanel({ subjectId }: { subjectId: string }): ReactElement {
  const canEdit = useCan("create_run_cases");
  const queryClient = useQueryClient();
  const [includeDetached, setIncludeDetached] = useState(false);

  const evidence = useQuery({
    queryKey: ["subject-evidence", subjectId, includeDetached],
    queryFn: () =>
      request<EvidenceLink[]>(
        `/research/subjects/${subjectId}/evidence?include_detached=${String(includeDetached)}`,
      ),
  });

  const detach = useMutation({
    mutationFn: (linkId: string) =>
      request<void>(`/research/subjects/${subjectId}/evidence/${linkId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subject-evidence", subjectId] });
      void queryClient.invalidateQueries({ queryKey: ["research-subjects"] });
    },
  });

  if (evidence.isLoading) return <Loading label="Loading evidence" />;
  if (evidence.error != null) return <ErrorState error={evidence.error} />;

  const links = evidence.data ?? [];

  return (
    <section className="space-y-3" aria-label="Evidence panel" data-testid="subject-panel-evidence">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-xs text-muted">
          Each row points at the assertion it came from. The source, title and captured
          bytes live there, so there is one version of them rather than two.
        </p>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            data-testid="evidence-include-detached"
            checked={includeDetached}
            onChange={(event) => setIncludeDetached(event.target.checked)}
          />
          Show excluded
        </label>
      </div>

      {detach.error != null && <ErrorState error={detach.error} />}

      {links.length === 0 ? (
        <EmptyState>
          Nothing attached yet. Attach the pages that persuaded you from a search result.
        </EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="evidence-list">
          {links.map((link) => (
            <li
              key={link.id}
              className="space-y-1 rounded border border-line p-3 text-sm"
              data-testid="evidence-row"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={link.detached_at == null ? "ok" : "muted"}>
                  {link.detached_at == null ? "Attached" : "Excluded"}
                </Badge>
                <span className="text-xs text-muted">{describe(link)}</span>
                <span className="text-xs text-muted">
                  {link.attached_at.slice(0, 19).replace("T", " ")}
                </span>
                {canEdit && link.detached_at == null && (
                  <button
                    type="button"
                    className="ml-auto text-xs text-muted hover:text-danger"
                    data-testid="evidence-detach"
                    onClick={() => detach.mutate(link.id)}
                  >
                    Exclude
                  </button>
                )}
              </div>
              <a
                className="block text-sm text-accent"
                data-testid="evidence-link"
                href={linkHref(link)}
                rel="noreferrer noopener"
                target="_blank"
              >
                {link.title !== "" ? link.title : linkHref(link)}
              </a>
              {link.snippet !== "" && <p className="text-xs text-muted">{link.snippet}</p>}
              {link.evidence_sha256 !== "" && (
                <span className="font-mono text-xs text-muted">
                  {link.evidence_sha256.slice(0, 12)}…
                </span>
              )}
              {link.rationale !== "" && link.rationale !== CANNED_RATIONALE && (
                <p className="text-xs text-muted">{link.rationale}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
